import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Locations } from '@/lib/api';
import { Select } from '@/design-system';
import { flattenLocationTree, LOCATION_KIND_ICON } from '@/lib/locations';

interface LocationPickerProps {
  value?: string;
  onChange: (locationId: string | undefined) => void;
  label?: string;
  unassignedLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * A single indented `<Select>` over the full location tree (depth shown via
 * "› " prefixes) rather than three cascading dropdowns — simpler to wire up
 * and works identically whether the tree is 1 or 3 levels deep. Self-fetches
 * the tree so it can be dropped into any drawer/modal without prop drilling.
 */
export function LocationPicker({ value, onChange, label, unassignedLabel, disabled, className }: LocationPickerProps) {
  const treeQ = useQuery({ queryKey: ['locations.tree'], queryFn: () => Locations.tree(), staleTime: 15_000 });
  const options = useMemo(() => flattenLocationTree(treeQ.data?.tree ?? []), [treeQ.data]);

  return (
    <Select
      label={label}
      className={className}
      disabled={disabled || treeQ.isLoading}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      options={[
        { value: '', label: unassignedLabel ?? 'Uten plassering' },
        ...options.map((opt) => ({
          value: opt.id,
          label: `${'› '.repeat(opt.depth)}${opt.name}`,
        })),
      ]}
    />
  );
}

export { LOCATION_KIND_ICON };
